import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { getdiagnosticos, createCoverage, resetSiscotelFlag, addComentario } from "../../../slices/thunks";
import { editSolicitud } from "../../../slices/thunks";
import { reenviarDiagnosticoSiscotel, getTasaDolar } from "../../../helpers/backend_helper";
import { limpiarDatosSolicitud, getMontoCobertura } from "../../../utils/solicitudDataCleaner";
import Select, { components } from "react-select";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Mapeo de cod1 a nombres de categorías
const CATEGORIAS_DIAGNOSTICO = {
  "01": "Enfermedades Infecciosas y Parasitarias",
  "02": "Neoplasias (Tumores)",
  "03": "Enfermedades de la Sangre",
  "04": "Enfermedades Endocrinas y Metabólicas",
  "05": "Trastornos Mentales",
  "06": "Enfermedades del Sistema Nervioso",
  "07": "Enfermedades del Ojo",
  "08": "Enfermedades del Oído",
  "09": "Enfermedades del Sistema Circulatorio",
  "10": "Enfermedades del Sistema Respiratorio",
  "11": "Enfermedades del Sistema Digestivo",
  "12": "Enfermedades de la Piel",
  "13": "Enfermedades del Sistema Osteomuscular",
  "14": "Enfermedades del Sistema Genitourinario",
  "15": "Embarazo, Parto y Puerperio",
  "16": "Afecciones del Período Perinatal",
  "17": "Malformaciones Congénitas",
  "18": "Síntomas y Signos Anormales",
  "19": "Traumatismos y Envenenamientos",
  "20": "Causas Externas de Morbilidad",
  "21": "Factores que Influyen en el Estado de Salud",
  "22": "Códigos para Propósitos Especiales",
};

/**
 * Normaliza texto para búsqueda: quita acentos y convierte a minúsculas
 */
const normalizarTexto = (texto) => {
  if (!texto) return "";
  return texto
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

/**
 * Función de filtrado personalizada para react-select
 * Busca en cualquier parte del nombre y también por código
 */
const filterOption = (option, inputValue) => {
  if (!inputValue) return true;
  
  const search = normalizarTexto(inputValue);
  const label = normalizarTexto(option.label);
  const code = option.value?.fullCode?.toLowerCase() || "";
  
  // Buscar en nombre O código
  return label.includes(search) || code.includes(search);
};

/**
 * Componente personalizado para mostrar opciones con código
 */
const FormatOptionLabel = ({ label, value }) => (
  <div className="d-flex align-items-center">
    <span 
      className="badge bg-light text-dark me-2" 
      style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
    >
      {value?.fullCode}
    </span>
    <span>{label}</span>
  </div>
);

/**
 * Componente personalizado para el encabezado de grupo
 */
const GroupHeading = (props) => (
  <components.GroupHeading {...props}>
    <div className="d-flex align-items-center py-1">
      <i className="ri-folder-line me-2 text-primary"></i>
      <span className="fw-semibold">{props.children}</span>
      <span className="badge bg-primary-subtle text-primary ms-2">
        {props.data.options.length}
      </span>
    </div>
  </components.GroupHeading>
);

import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  FormGroup,
  Label,
  Form,
  Button,
  Spinner,
} from "reactstrap";

const Diagnostico = ({ solicitud }) => {
  const dispatch = useDispatch();
  const [selectedMainDiagnostico, setSelectedMainDiagnostico] = useState(null);
  const [selectedSubDiagnostico, setSelectedSubDiagnostico] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDiagnostico, setShowDiagnostico] = useState(false);
  const [isTextVisible, setIsTextVisible] = useState(false);
  const [codDiagnostico, setCodDiagnostico] = useState(null);
  const [codSubDiagnostico, setCodSubDiagnostico] = useState(null);
  const [formErrors, setFormErrors] = useState({
    mainDiagnostico: false,
    subDiagnostico: false,
  });
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [loadingx, setLoadingx] = useState(false);
  const [currentSolicitudId, setCurrentSolicitudId] = useState(null);
  const [tasaDolar, setTasaDolar] = useState(null);

  const selectLayoutState = (state) => state;
  const pageData = createSelector(selectLayoutState, (state) => ({
    siscotel: state.Siscotel.siscotel,
    error: state.Siscotel.error,
    loading: state.Siscotel.loading,
    errorMsg: state.Siscotel.errorMsg,
    colorMsg: state.Siscotel.colorMsg,
    exito: state.Siscotel.exito,
  }));

  const { siscotel, error, loading, errorMsg, colorMsg, exito } =
    useSelector(pageData);

  // Cargar tasa del dólar al montar el componente
  useEffect(() => {
    const fetchTasaDolar = async () => {
      try {
        const response = await getTasaDolar();
        if (response.status === 200 && response.data) {
          setTasaDolar(response.data);
        }
      } catch (error) {
        console.error("Error al obtener tasa del dólar:", error);
      }
    };

    fetchTasaDolar();
  }, []);

  useEffect(() => {
    // Solo procesar si hay una respuesta de Siscotel y estamos en proceso de carga
    if (!siscotel || !loadingx) return;

    // Verificar si hay error de Siscotel (HTTP 200 pero status != 200 en body)
    if (solicitud?.aseguradora === "MyH+" && siscotel.siscotelStatus && siscotel.siscotelStatus !== 200) {
      setLoadingx(false);
      toast.error(`Error en MyH+: ${siscotel.siscotelAnswer || "Error desconocido"}`);
      return;
    }

    // Verificar respuesta exitosa (HTTP 200 Y siscotelStatus 200)
    if (solicitud?.aseguradora === "MyH+" && siscotel.status === 200 && siscotel.isSuccess !== false && siscotel.coverage) {
      // Parsear el valor de cobertura
      const coverageValue =
        typeof siscotel.coverage === "string"
          ? parseFloat(siscotel.coverage.replace(" $", ""))
          : typeof siscotel.coverage === "number"
          ? siscotel.coverage
          : 0;

      // Calcular porcentaje de avance basado en el estado actual
      // Si ya tiene un porcentaje mayor a 20, mantenerlo (no retroceder)
      // Solo establecer 20 si es la primera vez que se registra el diagnóstico
      const calcularPorcentajeAvance = () => {
        const porcentajeActual = solicitud.porcentaje_avance || 0;
        // Si ya tiene un porcentaje mayor a 20, mantenerlo
        if (porcentajeActual > 20) {
          return porcentajeActual;
        }
        // Si es nuevo o menor a 20, establecer 20
        return 20;
      };

      // Calcular subdiagnóstico directamente (puede ser null si no hay)
      const subcodDiagnosticoActual = selectedSubDiagnostico !== null
        ? selectedSubDiagnostico.value.fullCode
        : null;
      
      const nuevaSolicitud = {
        ...solicitud,
        codigoDiagnosticoFarmacologico: codDiagnostico,
        codigoSubdiagnosticoFarmacologico: subcodDiagnosticoActual,
        montoCobertura: coverageValue,
        porcentaje_avance: calcularPorcentajeAvance(),
        diagnosticoFarmacologico: selectedMainDiagnostico
          ? selectedMainDiagnostico.label
          : null,
        subdiagnosticoFarmacologico: selectedSubDiagnostico
          ? selectedSubDiagnostico.label
          : null,
      };

      dispatch(editSolicitud(limpiarDatosSolicitud(nuevaSolicitud))).then(() => {
        // Reenviar automáticamente a SISCOTEL si existe facturación
        const tieneFacturacion = solicitud?.facturacion && solicitud.facturacion.length > 0;
        
        if (tieneFacturacion && solicitud?.aseguradora === "MyH+") {
          // Reenviar diagnóstico a SISCOTEL automáticamente (solo MyH+)
          reenviarDiagnosticoSiscotel(solicitud.identificador)
            .then((response) => {
              if (response && response.status === 200) {
                // Agregar comentario en la bitácora
                const comentario = {
                  mensaje: `Diagnóstico farmacológico reenviado automáticamente: ${selectedMainDiagnostico?.label || 'N/A'}${selectedSubDiagnostico ? ` - ${selectedSubDiagnostico.label}` : ''}`,
                  tipo: "Enviado"
                };
                
                dispatch(addComentario({ identificador: solicitud.identificador, comentario }));
                
                toast.success(
                  "Diagnóstico registrado exitosamente en MyH+",
                  { position: "top-right", autoClose: 3000 }
                );
              }
            })
            .catch((error) => {
              console.error("Error al reenviar diagnóstico:", error);
              toast.warning(
                "Diagnóstico registrado pero no se pudo reenviar a MyH+",
                { position: "top-right", autoClose: 5000 }
              );
            });
        } else {
          toast.success(
            "Diagnóstico Farmacológico registrado exitosamente y hemos recibido el monto de la cobertura asociada",
            {}
          );
        }
      });
      
      setShowDiagnostico(true);
      setLoadingx(false);
    } else if (siscotel.status && siscotel.status !== 200) {
      // Error de Siscotel - usar siscotelAnswer que contiene el mensaje real de Siscotel
      setLoadingx(false);
      toast.error(
        `Error en MyH+: ${siscotel.siscotelAnswer || siscotel.message || 'Error desconocido'}`,
        {}
      );
    }
  }, [dispatch, siscotel, loadingx, solicitud, codDiagnostico, codSubDiagnostico, selectedMainDiagnostico, selectedSubDiagnostico]);

  useEffect(() => {
    dispatch(getdiagnosticos());
  }, [dispatch]);

  // Efecto para resetear los estados cuando cambia la solicitud
  useEffect(() => {
    if (solicitud) {
      // Si el ID de la solicitud ha cambiado
      if (currentSolicitudId !== solicitud.id) {
        // Resetear los estados
        setSelectedMainDiagnostico(null);
        setSelectedSubDiagnostico(null);
        setCodDiagnostico(null);
        setCodSubDiagnostico(null);
        setFormErrors({
          mainDiagnostico: false,
          subDiagnostico: false,
        });
        setShowErrorMessage(false);
        setLoadingx(false);
        
        // Limpiar el estado de Siscotel en Redux
        dispatch(resetSiscotelFlag());

        // Actualizar el ID de la solicitud actual
        setCurrentSolicitudId(solicitud.id);

        // Para Latina Salud, Hispana y Real Seguros: mostrar diagnóstico directamente si existe
        if ((solicitud.aseguradora?.toLowerCase() === "latina salud" || solicitud.aseguradora?.toLowerCase() === "hispana de seguros" || solicitud.aseguradora?.toLowerCase() === "real seguros") && solicitud.diagnostico) {
          setShowDiagnostico(true);
        }
        // Para otras aseguradoras: mostrar el diagnóstico si ya existe
        else if (
          solicitud.diagnosticoFarmacologico ||
          solicitud.subdiagnosticoFarmacologico
        ) {
          setShowDiagnostico(true);
        } else {
          setShowDiagnostico(false);
        }
      }

      setIsLoading(false);
    }
  }, [solicitud, dispatch, currentSolicitudId]);

  const selectLayoutStateDiagnostico = (state) => state;

  const diagnosticoPageData = createSelector(
    selectLayoutStateDiagnostico,
    (state) => ({
      diagnosticos: state.Diagnosticos.diagnosticos,
    })
  );

  const { diagnosticos } = useSelector(diagnosticoPageData);

  const handleMainDiagnosticoChange = (selectedOption) => {
    setSelectedMainDiagnostico(selectedOption);
    setSelectedSubDiagnostico(null);
    // Resetear errores
    setFormErrors({
      ...formErrors,
      mainDiagnostico: false,
      subDiagnostico: false,
    });
  };
  const handleSubDiagnosticoChange = (selectedOption) => {
    setSelectedSubDiagnostico(selectedOption);
    // Resetear error de subdiagnóstico
    setFormErrors({
      ...formErrors,
      subDiagnostico: false,
    });
  };


  const DiagnosticoTable = ({ solicitud, onEdit }) => {
    // Para Latina Salud, Hispana y Real Seguros: usar diagnóstico directo de la solicitud
    const esLatinaSalud = solicitud?.aseguradora?.toLowerCase() === "latina salud";
    const esHispana = solicitud?.aseguradora?.toLowerCase() === "hispana de seguros";
    const esRealSeguros = solicitud?.aseguradora?.toLowerCase() === "real seguros";
    const diagnosticoPrincipal = esLatinaSalud
      ? solicitud.diagnostico
      : (esHispana || esRealSeguros)
        ? (solicitud.diagnostico || solicitud.diagnosticoFarmacologico)
        : solicitud.diagnosticoFarmacologico;
    const subdiagnostico = esLatinaSalud
      ? null
      : solicitud.subdiagnosticoFarmacologico;

    if (
      !solicitud ||
      (!diagnosticoPrincipal && !subdiagnostico)
    ) {
      return (
        <div className="text-center p-5 bg-light rounded">
          <i className="ri-stethoscope-line text-muted font-size-24 mb-2"></i>
          <p className="text-muted mb-0">
            No hay información de diagnóstico disponible.
          </p>
        </div>
      );
    }

    return (
      <div className="card w-100">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="card-title mb-0">Información de Diagnóstico</h5>
          </div>

          <div className="table-responsive w-100">
            <table className="table table-centered table-hover w-100 mb-0" style={{ minHeight: "150px" }}>
              <thead className="table-light">
                <tr>
                  <th scope="col" className="py-3">Diagnóstico Principal</th>
                  <th scope="col" className="py-3">Subdiagnóstico</th>
                  <th
                    scope="col"
                    className="text-center py-3"
                    style={{ width: "100px" }}
                  >
                    Acción
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-3">
                    <span className="fw-medium">
                      {diagnosticoPrincipal || "N/A"}
                    </span>
                  </td>
                  <td className="py-3">{subdiagnostico || "N/A"}</td>
                  <td className="text-center py-3">
                    {(() => {
                      // Función para verificar si la solicitud fue actualizada hace más de 1 mes
                      const isOlderThanOneMonth = (updatedDate) => {
                        if (!updatedDate) return false;
                        const updated = new Date(updatedDate);
                        const oneMonthAgo = new Date();
                        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
                        return updated < oneMonthAgo;
                      };

                      // Verificar condiciones para deshabilitar edición
                      const shouldDisableEdit = 
                        solicitud.estatus === "Cancelado" ||
                        (solicitud.estatus === "Entregado" && isOlderThanOneMonth(solicitud.actualizado));

                      return shouldDisableEdit ? (
                        <span className="text-muted">-</span>
                      ) : (
                        <Button
                          color="soft-primary"
                          size="sm"
                          className="btn-icon rounded-circle"
                          onClick={onEdit}
                        >
                          <i className="ri-pencil-line"></i>
                        </Button>
                      );
                    })()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Card de Información de Cobertura - Solo mostrar si no hay cobertura */}
        {(() => {
          // Usar helper para extraer monto de cobertura (maneja objeto y número)
          const montoCobertura = getMontoCobertura(solicitud.montoCobertura);
          
          return (!montoCobertura || montoCobertura <= 0) && (
            <div className="card mt-3" style={{ backgroundColor: '#fff7ed', border: '1px solid #fed7aa' }}>
              <div className="card-body py-3">
                <div className="d-flex align-items-center gap-2">
                  <div className="avatar-xs">
                    <span className="avatar-title rounded-circle bg-warning">
                      <i className="ri-error-warning-line text-white"></i>
                    </span>
                  </div>
                  <div>
                    <p className="text-muted mb-0 small">Monto de Cobertura</p>
                    <h5 className="mb-0">
                      <span className="text-warning">Sin cobertura asignada</span>
                    </h5>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  const handleSubmitDiagnostico = (e) => {
    e.preventDefault();

    // Validar campos obligatorios
    const errors = {
      mainDiagnostico: !selectedMainDiagnostico,
      subDiagnostico:
        selectedMainDiagnostico &&
        subDiagnosticos.length > 0 &&
        !selectedSubDiagnostico,
    };

    // Actualizar el estado de los errores
    setFormErrors(errors);

    // Si hay algún error, mostrar mensaje y detener la ejecución
    if (Object.values(errors).some((error) => error)) {
      setShowErrorMessage(true);
      setTimeout(() => setShowErrorMessage(false), 3000);
      return;
    }

    // Solo establecer loading en true si la validación es exitosa
    setLoadingx(true);

    const subcodDiagnostico =
      selectedSubDiagnostico !== null
        ? selectedSubDiagnostico.value.fullCode
        : null;

    const codDiagnostico = selectedMainDiagnostico.value.fullCode;

    setCodDiagnostico(codDiagnostico);
    setCodSubDiagnostico(subcodDiagnostico);

    const cobertura = {
      CedulaTitular: solicitud.titularCedula,
      CedulaSolicitante: solicitud.solicitanteCedula,
      CodigoDiagnostico: codDiagnostico,
      CodigoProveedor: solicitud.codigoProveedor,
    };

    if (solicitud?.aseguradora === "Seguros Mercantil" || solicitud?.aseguradora?.toLowerCase() === "hispana de seguros" || solicitud?.aseguradora?.toLowerCase() === "real seguros") {
      // Para Seguros Mercantil, Hispana y Real Seguros: guardar diagnóstico directamente sin consultar SISCOTEL
      const calcularPorcentajeAvanceMercantil = () => {
        const porcentajeActual = solicitud.porcentaje_avance || 0;
        return porcentajeActual > 20 ? porcentajeActual : 20;
      };

      // Calcular subdiagnóstico directamente (puede ser null si no hay)
      const subcodDiagnosticoActual = selectedSubDiagnostico !== null
        ? selectedSubDiagnostico.value.fullCode
        : null;
      
      const nuevaSolicitudMercantil = {
        ...solicitud,
        codigoDiagnosticoFarmacologico: codDiagnostico,
        codigoSubdiagnosticoFarmacologico: subcodDiagnosticoActual,
        porcentaje_avance: calcularPorcentajeAvanceMercantil(),
        diagnosticoFarmacologico: selectedMainDiagnostico ? selectedMainDiagnostico.label : null,
        subdiagnosticoFarmacologico: selectedSubDiagnostico ? selectedSubDiagnostico.label : null,
      };

      dispatch(editSolicitud(limpiarDatosSolicitud(nuevaSolicitudMercantil))).then(() => {
        setShowDiagnostico(true);
        setLoadingx(false);
        toast.success("Diagnóstico Farmacológico registrado exitosamente", {});
      }).catch(() => {
        setLoadingx(false);
        toast.error("Error al registrar el diagnóstico", {});
      });
    } else {
      // Para MyH+: consultar cobertura a SISCOTEL (el useEffect maneja el guardado)
      try {
        if (cobertura && cobertura.CodigoDiagnostico) {
          dispatch(createCoverage(cobertura))
            .then(() => {
              console.info("Solicitud del monto de cobertura");
            })
            .catch((error) => {
              console.error("Error al solicitar monto de cobertura:", error);
              setLoadingx(false);
            });
        }
      } catch (error) {
        console.error("Error al solicitar monto de cobertura:", error);
        setLoadingx(false);
      }
    }
  };

  // Generar diagnósticos agrupados por categoría usando useMemo para optimizar
  const mainDiagnosticosAgrupados = useMemo(() => {
    console.log('🔍 DEBUG - Generando mainDiagnosticosAgrupados');
    console.log('📊 diagnosticos:', diagnosticos);
    console.log('🏥 Aseguradora:', solicitud?.aseguradora);
    
    if (!diagnosticos) {
      console.warn('⚠️ No hay diagnosticos disponibles');
      return [];
    }
    
    console.log('📈 Total diagnósticos en BD:', diagnosticos.length);
    
    // Filtrar solo diagnósticos principales (sin sub_codigo), incluyendo
    // headers de bloque/capítulo CIE-10 (ej. "EMBARAZO, PARTO Y PUERPERIO (O00-O99)")
    const diagnosticosPrincipales = diagnosticos
      .filter((diag) => diag.sub_codigo === null)
      .map((diag) => ({
        value: {
          fullCode: `${diag.cod1}${diag.cod2}${diag.cod3}${diag.cod4 || ""}`,
          cod4: diag.cod4,
          cod1: diag.cod1,
        },
        label: diag.nombre,
        categoria: diag.cod1,
        frecuente: diag.frecuente === "1",
      }));
    
    console.log('📋 Diagnósticos principales filtrados:', diagnosticosPrincipales.length);
    
    // Separar frecuentes
    const frecuentes = diagnosticosPrincipales
      .filter(d => d.frecuente)
      .sort((a, b) => a.label.localeCompare(b.label));
    
    // Agrupar el resto por categoría
    const porCategoria = {};
    diagnosticosPrincipales
      .filter(d => !d.frecuente)
      .forEach(diag => {
        const cat = diag.categoria;
        if (!porCategoria[cat]) {
          porCategoria[cat] = [];
        }
        porCategoria[cat].push(diag);
      });
    
    // Construir array de grupos
    const grupos = [];
    
    // Primero los frecuentes si hay
    if (frecuentes.length > 0) {
      grupos.push({
        label: "⭐ Diagnósticos Frecuentes",
        options: frecuentes,
      });
    }
    
    // Luego las categorías ordenadas
    Object.keys(porCategoria)
      .sort()
      .forEach(cat => {
        const nombreCategoria = CATEGORIAS_DIAGNOSTICO[cat] || `Categoría ${cat}`;
        grupos.push({
          label: nombreCategoria,
          options: porCategoria[cat].sort((a, b) => a.label.localeCompare(b.label)),
        });
      });
    
    console.log('✅ Total grupos generados:', grupos.length);
    console.log('📦 Grupos:', grupos.map(g => `${g.label} (${g.options.length})`));
    
    return grupos;
  }, [diagnosticos, solicitud?.aseguradora]);

  const subDiagnosticos = useMemo(() => {
    if (!diagnosticos || !selectedMainDiagnostico) return [];
    
    return diagnosticos
      .filter(
        (diag) =>
          diag.cod4 === selectedMainDiagnostico.value.cod4 &&
          diag.sub_codigo !== null
      )
      .sort((a, b) => a.nombre.localeCompare(b.nombre))
      .map((diag) => ({ 
        value: {
          sub_codigo: diag.sub_codigo,
          fullCode: `${diag.cod1}${diag.cod2}${diag.cod3}${diag.cod4}${diag.sub_codigo}`,
        },
        label: diag.nombre,
      }));
  }, [diagnosticos, selectedMainDiagnostico]);

  const editarSolicitudDiagnostico = () => {
    setShowDiagnostico(false);
  };

  if (isLoading) {
    return <div>Cargando...</div>;
  }

  return (
    <React.Fragment>
      <Col>
        <Card className="border-0 shadow-sm">
          <CardHeader className="bg-white border-bottom border-light py-3">
            <div className="d-flex align-items-center">
              <i className="ri-stethoscope-line text-primary me-2 fs-20"></i>
              <h5 className="card-title mb-0">
                Diagnóstico & Consulta de Cobertura
              </h5>
            </div>
          </CardHeader>
          <CardBody className="p-4">
            {/* Verificar si los documentos están verificados */}
            {!solicitud?.verificacionDocumentos?.verificado && !showDiagnostico ? (
              // Bloqueo: Documentos no verificados
              <div className="text-center p-4">
                <div className="avatar-md mx-auto mb-3">
                  <div className="avatar-title bg-warning-subtle text-warning rounded-circle fs-24">
                    <i className="ri-lock-line"></i>
                  </div>
                </div>
                <h5 className="text-warning mb-2">Verificación Pendiente</h5>
                <p className="text-muted mb-0">
                  Debe verificar los documentos médicos (recipe e informe) antes de seleccionar el diagnóstico.
                </p>
                <p className="text-muted small mt-2">
                  <i className="ri-arrow-up-line me-1"></i>
                  Revise la sección de documentos arriba y confirme la verificación.
                </p>
              </div>
            ) : !showDiagnostico ? (
              <Form onSubmit={handleSubmitDiagnostico}>
                {showErrorMessage && (
                  <div
                    className="alert alert-danger d-flex align-items-center"
                    role="alert"
                  >
                    <i className="ri-error-warning-line me-2 fs-18"></i>
                    <div>Por favor complete todos los campos obligatorios</div>
                  </div>
                )}

                <div className="bg-light p-4 rounded mb-4">
                  <Row>
                    <Col md={6} className="mb-3">
                      <FormGroup>
                        <Label
                          for="mainDiagnosticoSelect"
                          className="form-label fw-medium"
                        >
                          Diagnóstico principal{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <Select
                          id="mainDiagnosticoSelect"
                          options={mainDiagnosticosAgrupados}
                          value={selectedMainDiagnostico}
                          onChange={handleMainDiagnosticoChange}
                          placeholder="Buscar por nombre o código..."
                          isClearable
                          isSearchable
                          filterOption={filterOption}
                          formatOptionLabel={FormatOptionLabel}
                          components={{ GroupHeading }}
                          noOptionsMessage={() => "No se encontraron diagnósticos"}
                          loadingMessage={() => "Buscando..."}
                          className={
                            formErrors.mainDiagnostico ? "is-invalid" : ""
                          }
                          classNamePrefix="select"
                          styles={{
                            control: (base) => ({
                              ...base,
                              borderColor: formErrors.mainDiagnostico
                                ? "#dc3545"
                                : base.borderColor,
                              boxShadow: formErrors.mainDiagnostico
                                ? "0 0 0 0.25rem rgba(220, 53, 69, 0.25)"
                                : base.boxShadow,
                              "&:hover": {
                                borderColor: formErrors.mainDiagnostico
                                  ? "#dc3545"
                                  : base["&:hover"].borderColor,
                              },
                            }),
                            groupHeading: (base) => ({
                              ...base,
                              backgroundColor: "#f8f9fa",
                              padding: "8px 12px",
                              fontWeight: 600,
                              color: "#495057",
                              borderBottom: "1px solid #e9ecef",
                            }),
                            group: (base) => ({
                              ...base,
                              paddingTop: 0,
                              paddingBottom: 0,
                            }),
                            option: (base, state) => ({
                              ...base,
                              padding: "8px 12px",
                              backgroundColor: state.isFocused ? "#e7f1ff" : "white",
                              color: "#212529",
                            }),
                            menuList: (base) => ({
                              ...base,
                              maxHeight: "350px",
                            }),
                          }}
                        />
                        {formErrors.mainDiagnostico && (
                          <div className="text-danger small mt-1">
                            <i className="ri-error-warning-line me-1"></i>
                            Por favor seleccione un diagnóstico principal
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={6} className="mb-3">
                      <FormGroup>
                        <Label
                          for="subDiagnosticoSelect"
                          className="form-label fw-medium"
                        >
                          Sub-diagnóstico{" "}
                          {selectedMainDiagnostico &&
                            subDiagnosticos.length > 0 && (
                              <span className="text-danger">*</span>
                            )}
                        </Label>
                        <Select
                          id="subDiagnosticoSelect"
                          options={subDiagnosticos}
                          value={selectedSubDiagnostico}
                          onChange={handleSubDiagnosticoChange}
                          placeholder={
                            subDiagnosticos.length > 0
                              ? "Buscar sub-diagnóstico..."
                              : "No hay sub-diagnósticos disponibles"
                          }
                          isDisabled={!selectedMainDiagnostico}
                          isClearable
                          isSearchable
                          filterOption={(option, inputValue) => {
                            if (!inputValue) return true;
                            const search = normalizarTexto(inputValue);
                            const label = normalizarTexto(option.label);
                            return label.includes(search);
                          }}
                          noOptionsMessage={() => "No se encontraron sub-diagnósticos"}
                          className={
                            formErrors.subDiagnostico ? "is-invalid" : ""
                          }
                          classNamePrefix="select"
                          styles={{
                            control: (base) => ({
                              ...base,
                              borderColor: formErrors.subDiagnostico
                                ? "#dc3545"
                                : base.borderColor,
                              boxShadow: formErrors.subDiagnostico
                                ? "0 0 0 0.25rem rgba(220, 53, 69, 0.25)"
                                : base.boxShadow,
                              "&:hover": {
                                borderColor: formErrors.subDiagnostico
                                  ? "#dc3545"
                                  : base["&:hover"].borderColor,
                              },
                            }),
                            menuList: (base) => ({
                              ...base,
                              maxHeight: "250px",
                            }),
                          }}
                        />
                        {formErrors.subDiagnostico && (
                          <div className="text-danger small mt-1">
                            <i className="ri-error-warning-line me-1"></i>
                            Por favor seleccione un sub-diagnóstico
                          </div>
                        )}
                        {selectedMainDiagnostico &&
                          subDiagnosticos.length === 0 && (
                            <div className="text-muted small mt-1">
                              <i className="ri-information-line me-1"></i>
                              Este diagnóstico no tiene sub-diagnósticos
                              disponibles
                            </div>
                          )}
                      </FormGroup>
                    </Col>
                  </Row>

                  {solicitud.estatus !== "Cancelado" &&
                    solicitud.estatus !== "Entregado" && (
                      <div className="d-flex justify-content-end gap-2 mt-4">
                        {/* Botón de Cancelar */}
                        <Button
                          color="light"
                          className="d-flex align-items-center"
                          onClick={() => {
                            // Resetear el formulario
                            setSelectedMainDiagnostico(null);
                            setSelectedSubDiagnostico(null);
                            setFormErrors({});
                            setShowErrorMessage(false);
                            
                            // Solo mostrar la tabla si ya existe un diagnóstico
                            if (solicitud.diagnosticoFarmacologico || solicitud.subdiagnosticoFarmacologico) {
                              setShowDiagnostico(true);
                            }
                          }}
                        >
                          <i className="ri-close-line me-1"></i>
                          Cancelar
                        </Button>

                        {/* Botón de Registrar */}
                        <Button
                          type="submit"
                          color="success"
                          className="d-flex align-items-center"
                          disabled={loadingx}
                        >
                          {loadingx ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Registrando...
                            </>
                          ) : (
                            <>
                              <i className="ri-save-line me-1"></i>
                              Registrar Diagnóstico
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                </div>
              </Form>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <DiagnosticoTable
                    solicitud={solicitud}
                    onEdit={editarSolicitudDiagnostico}
                  />

                  {(() => {
                    // Función para verificar si la solicitud fue actualizada hace más de 1 mes
                    const isOlderThanOneMonth = (updatedDate) => {
                      if (!updatedDate) return false;
                      const updated = new Date(updatedDate);
                      const oneMonthAgo = new Date();
                      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
                      return updated < oneMonthAgo;
                    };

                    // Verificar condiciones para mostrar botón de edición
                    const shouldShowEdit = 
                      solicitud.estatus !== "Cancelado" &&
                      (solicitud.estatus !== "Entregado" || !isOlderThanOneMonth(solicitud.actualizado));

                    return shouldShowEdit && (
                      <Button
                        color="light"
                        size="sm"
                        className="position-absolute top-0 end-0 mt-3 me-4"
                        onClick={editarSolicitudDiagnostico}
                      >
                        <i className="ri-pencil-line me-1"></i>
                        Editar
                      </Button>
                    );
                  })()}
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default Diagnostico;
